import React, { useEffect, useMemo, useState } from "react";
import SideBarStyle from "../../../assets/stylesheets/farm-detail/sections/SideBar.module.css";

function formatTemp(temperature) {
  if (!temperature || typeof temperature.degrees !== "number") return "—";
  const rounded = Math.round(temperature.degrees);
  const unit = temperature.unit;

  if (unit === "CELSIUS") return `${rounded}°C`;
  if (unit === "FAHRENHEIT") return `${rounded}°F`;
  return `${rounded}°`;
}

function formatForecastDayLabel(day, timeZone) {
  const tz = timeZone || "UTC";

  // Prefer the API interval startTime (an instant). This avoids day shifting
  // that happens when you manufacture a Date from displayDate at UTC midnight.
  const startTime = day?.interval?.startTime;
  if (typeof startTime === "string" && startTime) {
    const date = new Date(startTime);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: tz,
      }).format(date);
    }
  }

  // Fallback: use displayDate but pick UTC noon to reduce timezone day rollover.
  const displayDate = day?.displayDate;
  if (!displayDate?.year || !displayDate?.month || !displayDate?.day) return "";
  const safeDate = new Date(Date.UTC(displayDate.year, displayDate.month - 1, displayDate.day, 12));
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: tz,
  }).format(safeDate);
}

function getIconUrl(iconBaseUri, isDark = false) {
  if (!iconBaseUri) return null;
  return `${iconBaseUri}${isDark ? "_dark" : ""}.svg`;
}

function parseIsoMs(value) {
  if (typeof value !== "string" || !value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function getCurrentTimeMs(current) {
  if (!current || typeof current !== "object") return Date.now();
  const candidates = [
    current.currentTime,
    current.updateTime,
    current.observationTime,
    current.asOfTime,
    current?.metadata?.updateTime,
  ];

  for (const iso of candidates) {
    const ms = parseIsoMs(iso);
    if (ms != null) return ms;
  }

  return Date.now();
}

function isDaylightNow({ nowMs, forecastDays }) {
  const days = Array.isArray(forecastDays) ? forecastDays : [];
  if (!days.length) return true;

  const containingDay = days.find((d) => {
    const startMs = parseIsoMs(d?.interval?.startTime);
    const endMs = parseIsoMs(d?.interval?.endTime);
    if (startMs == null || endMs == null) return false;
    return nowMs >= startMs && nowMs < endMs;
  });

  const day = containingDay || days[0];
  const sunriseMs = parseIsoMs(day?.sunEvents?.sunriseTime);
  const sunsetMs = parseIsoMs(day?.sunEvents?.sunsetTime);
  if (sunriseMs == null || sunsetMs == null) return true;

  return nowMs >= sunriseMs && nowMs < sunsetMs;
}

async function lookupCountryCode({ lat, lng, apiKey, signal }) {
  // Uses Google Maps Geocoding API (reverse geocode) to determine the country of the farm.
  // This avoids relying on the user's browser language/locale.
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
    `${lat},${lng}`
  )}&key=${encodeURIComponent(apiKey)}&result_type=country`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Geocoding failed (${res.status})`);
  const json = await res.json();

  const results = Array.isArray(json?.results) ? json.results : [];
  for (const r of results) {
    const components = Array.isArray(r?.address_components) ? r.address_components : [];
    for (const c of components) {
      const types = Array.isArray(c?.types) ? c.types : [];
      if (types.includes("country") && typeof c?.short_name === "string") {
        return c.short_name.toUpperCase();
      }
    }
  }

  return null;
}

async function lookupTimeZoneId({ lat, lng, apiKey, signal }) {
  // Uses Google Time Zone API to format dates in the farm's local timezone.
  // https://developers.google.com/maps/documentation/timezone
  const timestamp = Math.floor(Date.now() / 1000);
  const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${encodeURIComponent(
    `${lat},${lng}`
  )}&timestamp=${timestamp}&key=${encodeURIComponent(apiKey)}`;

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Time Zone lookup failed (${res.status})`);
  const json = await res.json();

  if (json?.status === "OK" && typeof json?.timeZoneId === "string") return json.timeZoneId;
  return null;
}

export default function WeatherInfo({ coords }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const IMPERIAL_REGIONS = useMemo(() => new Set(["US", "LR", "MM"]), []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [forecastDays, setForecastDays] = useState([]);
  const [unitsSystem, setUnitsSystem] = useState("METRIC");
  const [timeZoneId, setTimeZoneId] = useState(null);

  useEffect(() => {
    if (!coords?.lat || !coords?.lng) return;
    if (!apiKey) return;

    setTimeZoneId(null);

    const abortController = new AbortController();
    const { signal } = abortController;

    lookupTimeZoneId({ lat: coords.lat, lng: coords.lng, apiKey, signal })
      .then((tz) => {
        if (tz) setTimeZoneId(tz);
      })
      .catch(() => {
        // If Time Zone API isn't enabled/restricted, fall back to UTC.
      });

    return () => abortController.abort();
  }, [coords?.lat, coords?.lng, apiKey]);

  useEffect(() => {
    if (!coords?.lat || !coords?.lng) return;
    if (!apiKey) return;

    // Default to METRIC unless we can positively identify an imperial-only country.
    // This ensures Canada stays metric even if the user's browser locale is en-US.
    setUnitsSystem("METRIC");

    const abortController = new AbortController();
    const { signal } = abortController;

    lookupCountryCode({ lat: coords.lat, lng: coords.lng, apiKey, signal })
      .then((countryCode) => {
        if (!countryCode) return;
        setUnitsSystem(IMPERIAL_REGIONS.has(countryCode) ? "IMPERIAL" : "METRIC");
      })
      .catch(() => {
        // If geocoding isn't enabled/restricted, keep METRIC fallback.
      });

    return () => abortController.abort();
  }, [coords?.lat, coords?.lng, apiKey, IMPERIAL_REGIONS]);

  useEffect(() => {
    if (!coords?.lat || !coords?.lng) return;
    if (!apiKey) {
      setError("Missing Google API key");
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const paramsBase = new URLSearchParams({
      key: apiKey,
      "location.latitude": String(coords.lat),
      "location.longitude": String(coords.lng),
      unitsSystem,
      languageCode: "en",
    });

    const currentUrl = `https://weather.googleapis.com/v1/currentConditions:lookup?${paramsBase.toString()}`;

    const dailyParams = new URLSearchParams(paramsBase);
    dailyParams.set("days", "5");
    dailyParams.set("pageSize", "5");
    const dailyUrl = `https://weather.googleapis.com/v1/forecast/days:lookup?${dailyParams.toString()}`;

    setLoading(true);
    setError(null);

    Promise.all([
      fetch(currentUrl, { signal }).then(async (r) => {
        if (!r.ok) throw new Error(`Weather current failed (${r.status})`);
        return r.json();
      }),
      fetch(dailyUrl, { signal }).then(async (r) => {
        if (!r.ok) throw new Error(`Weather forecast failed (${r.status})`);
        return r.json();
      }),
    ])
      .then(([currentJson, dailyJson]) => {
        setCurrent(currentJson);
        setForecastDays(Array.isArray(dailyJson?.forecastDays) ? dailyJson.forecastDays : []);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Failed to load weather");
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => abortController.abort();
  }, [coords?.lat, coords?.lng, apiKey, unitsSystem]);

  const currentIconUrl = getIconUrl(current?.weatherCondition?.iconBaseUri, false);
  const currentDescription = current?.weatherCondition?.description?.text;
  const currentTemp = formatTemp(current?.temperature);

  const isDaylight = useMemo(() => {
    const nowMs = getCurrentTimeMs(current);
    return isDaylightNow({ nowMs, forecastDays });
  }, [current, forecastDays]);

  return (
    <div className={SideBarStyle["weather-card"]}>
      <p className={SideBarStyle["weather-title"]}>Weather</p>

      {!coords ? (
        <p className={SideBarStyle["weather-placeholder"]}>
          Weather unavailable (missing location).
        </p>
      ) : !apiKey ? (
        <p className={SideBarStyle["weather-placeholder"]}>
          Weather unavailable (missing API key).
        </p>
      ) : loading ? (
        <p className={SideBarStyle["weather-placeholder"]}>Loading weather…</p>
      ) : error ? (
        <p className={SideBarStyle["weather-placeholder"]}>Weather unavailable.</p>
      ) : (
        <div className={SideBarStyle["weather-body"]}>
          <div className={SideBarStyle["weather-current"]}>
            {currentIconUrl ? (
              <span
                className={[
                  SideBarStyle["weather-icon-wrap"],
                  isDaylight
                    ? SideBarStyle["weather-icon-wrap-day"]
                    : SideBarStyle["weather-icon-wrap-night"],
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <img
                  className={SideBarStyle["weather-icon"]}
                  src={currentIconUrl}
                  alt={currentDescription || "Current weather"}
                />
              </span>
            ) : null}
            <div className={SideBarStyle["weather-current-text"]}>
              <div className={SideBarStyle["weather-current-temp"]}>{currentTemp}</div>
              <div className={SideBarStyle["weather-current-desc"]}>
                {currentDescription || ""}
              </div>
            </div>
          </div>

          <div className={SideBarStyle["weather-forecast"]}>
            {(forecastDays || []).slice(0, 5).map((day) => {
              const dateLabel = formatForecastDayLabel(day, timeZoneId);
              const dayDesc = day?.daytimeForecast?.weatherCondition?.description?.text;
              const iconUrl = getIconUrl(day?.daytimeForecast?.weatherCondition?.iconBaseUri, false);
              const hi = formatTemp(day?.maxTemperature);
              const lo = formatTemp(day?.minTemperature);

              return (
                <div
                  className={SideBarStyle["weather-row"]}
                  key={day?.interval?.startTime || `${day?.displayDate?.year}-${day?.displayDate?.month}-${day?.displayDate?.day}`}
                >
                  <div className={SideBarStyle["weather-row-date"]}>{dateLabel}</div>
                  <div className={SideBarStyle["weather-row-mid"]}>
                    {iconUrl ? (
                      <span className={SideBarStyle["weather-row-icon-wrap"]}>
                        <img
                          className={SideBarStyle["weather-row-icon"]}
                          src={iconUrl}
                          alt={dayDesc || "Forecast"}
                        />
                      </span>
                    ) : null}
                    <span className={SideBarStyle["weather-row-desc"]}>{dayDesc || ""}</span>
                  </div>
                  <div className={SideBarStyle["weather-row-temps"]}>{hi} / {lo}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}